// ============================================================================
// PROMOTION FEATURE — "Founding Customer Pricing" (promo code FOUNDER)
// ----------------------------------------------------------------------------
// LIVE / DB-DRIVEN: active status and discount come from the real FOUNDER promo
// code in the database (admin → Promo codes) via GET /api/promos/active — the
// SAME source checkout uses. FOUNDER has NO end date: it stays live until an
// admin deactivates it, and every promo display (homepage banner, ribbon, promo
// page, structured data) then disappears automatically with no deploy.
// This file owns the bilingual marketing COPY; {promoPrice}/{regular}/{rebate}
// tokens are filled from the live promo at render time.
// ============================================================================
import { useEffect, useState } from "react";
import axios from "axios";

export const PROMO = {
  name: "Founding Customer Pricing",
  regularPrice: 10, // regular $ per 18 L bottle
  rebateAmount: 2, // FALLBACK $ rebate per bottle (only if the live promo can't be read)
  minimumOrder: 4,
  code: "FOUNDER",
  size: "18 L",
  serviceArea: "Ottawa, Ontario",
  pageUrl: "https://capitalflow.ca/ottawa-water-delivery-promotion",
  pagePath: "/ottawa-water-delivery-promotion",
  orderPath: "/#order",
  partnersPath: "/ottawa-business-partners",
  phone: "343-543-1714",
  phoneRaw: "3435431714",
  email: "sales@capitalflow.ca",
};

// LIVE promo — reads the FOUNDER code from the public feed (active codes only).
// Never blocks the UI: on any error it resolves to inactive.
export function usePromoLive() {
  const [state, setState] = useState({ loading: true, active: false, promo: null });
  useEffect(() => {
    let alive = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/promos/active`)
      .then((r) => {
        if (!alive) return;
        const list = (r.data && r.data.promos) || [];
        const target = list.find((p) => (p.code || "").toUpperCase() === PROMO.code);
        setState({ loading: false, active: !!target, promo: target || null });
      })
      .catch(() => {
        if (alive) setState({ loading: false, active: false, promo: null });
      });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}

export function buildPromoView(promo, lang = "en") {
  const rebate =
    promo && promo.discount_type === "per_bottle_fixed"
      ? (promo.discount_value_cents || 0) / 100
      : PROMO.rebateAmount;
  const regular = PROMO.regularPrice;
  const promoPrice = Math.max(0, Math.round((regular - rebate) * 100) / 100);
  const vars = { promoPrice: String(promoPrice), regular: String(regular), rebate: String(rebate), size: PROMO.size };
  return { rebate, regular, promoPrice, vars, text: resolvePromoText(lang, vars) };
}

// FOUNDER requires MANUAL entry at checkout (backend only discounts when a promo_code is supplied).
export const PROMO_REQUIRES_MANUAL_CODE = true;

// Lightweight click tracking (no-op until GA/GTM is present). Never breaks the UI.
export function trackPromo(event, params = {}) {
  try {
    if (typeof window === "undefined") return;
    const payload = { promo_code: PROMO.code, ...params };
    if (typeof window.gtag === "function") window.gtag("event", event, payload);
    else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...payload });
  } catch (e) { /* analytics must never break the UI */ }
}

function fillStr(s, vars) {
  return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
}

export function resolvePromoText(lang, vars) {
  const base = PROMO_TEXT[lang] || PROMO_TEXT.en;
  const out = {};
  for (const [k, v] of Object.entries(base)) {
    if (typeof v === "string") out[k] = fillStr(v, vars);
    else if (Array.isArray(v)) out[k] = v.map((x) => fillStr(x, vars));
    else out[k] = v;
  }
  return out;
}

export const PROMO_TEXT = {
  en: {
    label: "Founding Customer Pricing",
    bannerPrice: "${promoPrice} per {size} bottle",
    bannerRegular: "Regularly ${regular}",
    availability: "Available while delivery spots remain open on our current Ottawa routes.",
    codeLead: "Use promo code",
    codeTail: "at checkout",
    codeLabel: "Promo code",
    cta: "Reserve Your Delivery Spot",
    details: "Details",
    copy: "Copy code",
    copied: "Copied!",
    seoTitle: "Founding Customer Pricing: ${promoPrice} / 18 L Bottle | Capital Flow",
    seoDesc:
      "Capital Flow's Founding Customer Pricing: ${promoPrice} per {size} bottle (regularly ${regular}) with promo code FOUNDER, " +
      "available while delivery spots remain open on our current Ottawa routes. Minimum 4 bottles, no setup fees.",
    h1: "Founding Customer Pricing",
    intro: [
      "Capital Flow is growing and currently building its Ottawa delivery routes.",
      "While delivery spots remain available, new customers can take advantage of our Founding Customer Price of ${promoPrice} per {size} bottle, regularly ${regular}.",
      "Once our current delivery routes reach capacity, regular pricing will apply to new customers.",
    ],
    promotional: "Founding Customer Price",
    regular: "Regular price",
    save: "You save",
    perBottle: "per 18 L bottle",
    rebatePer: "per bottle",
    terms: [
      "${promoPrice} per {size} bottle",
      "Regular price ${regular}",
      "Minimum order of 4 bottles",
      "Residential and business delivery",
      "Weekly, bi-weekly or monthly delivery",
      "No setup fees",
      "Promo code: FOUNDER",
      "Available while delivery spots remain open on our Ottawa routes",
      "Founding Customers on recurring delivery keep ${promoPrice} per {size} bottle for as long as their recurring service continues",
    ],
    termsNote: "Applies to 18 L bottled water only. Refundable bottle deposits, dispensers, equipment and other products or fees are not discounted.",
    howToTitle: "How to get the ${promoPrice} price",
    howToBody: "Add your bottles, then enter FOUNDER in the “Promo code” field at checkout and tap Apply.",
    lppTitle: "Local Partner Program",
    lppLead: "Free for businesses scheduling a minimum of 10 bottles per month.",
    lppBenefits: [
      "Business profile on CapitalFlow.ca",
      "Featured promotion of participating businesses",
      "Opportunities to be featured in Capital Flow content/blogs",
      "Prizes and recognition for top participating partners",
    ],
    lppCta: "Join the Local Partner Program",
    faqTitle: "Frequently asked",
    faqQ: "How much does Capital Flow's 18 L water delivery cost with Founding Customer Pricing?",
    faqA: "While delivery spots remain open on our current Ottawa routes, new customers pay ${promoPrice} per {size} bottle instead of the regular ${regular} price with promo code FOUNDER. The minimum order is 4 bottles and there are no setup fees. Refundable bottle deposits are separate and are not discounted.",
    order: "Order water",
    endedH1: "Founding Customer Pricing is now closed",
    endedBody: "Our current Ottawa delivery routes have filled up, so regular pricing now applies to new customers: ${regular} per 18 L bottle, with a four-bottle minimum and no setup fees. Refundable bottle deposits are separate.",
    endedTitle: "Ottawa Water Delivery | Capital Flow",
    endedDesc: "Capital Flow delivers 18 L and 11 L bottled water across Ottawa. See our current water delivery offer and order online.",
    endedCta: "See current water delivery offer",
    back: "Back to Capital Flow",
    ribbon: "Founding Customer Pricing: ${promoPrice} per {size} bottle with code FOUNDER",
    line: "Founding Customer Pricing: ${promoPrice} per {size} bottle (regularly ${regular}) with promo code FOUNDER — while delivery spots remain open on our Ottawa routes.",
  },
  fr: {
    label: "Tarif client fondateur",
    bannerPrice: "{promoPrice} $ par bouteille de {size}",
    bannerRegular: "Prix régulier : {regular} $",
    availability: "Offert tant qu'il reste des places disponibles sur nos routes de livraison actuelles à Ottawa.",
    codeLead: "Utilisez le code promo",
    codeTail: "au moment du paiement",
    codeLabel: "Code promo",
    cta: "Réservez votre place de livraison",
    details: "Détails",
    copy: "Copier le code",
    copied: "Copié!",
    seoTitle: "Tarif client fondateur : {promoPrice} $ / 18 L | Capital Flow",
    seoDesc:
      "Tarif client fondateur de Capital Flow : {promoPrice} $ par bouteille de {size} (prix régulier {regular} $) avec le code FOUNDER, " +
      "tant qu'il reste des places sur nos routes de livraison actuelles à Ottawa. Minimum de 4 bouteilles, aucuns frais d'installation.",
    h1: "Tarif client fondateur",
    intro: [
      "Capital Flow est en pleine croissance et développe actuellement ses routes de livraison à Ottawa.",
      "Tant qu'il reste des places disponibles, les nouveaux clients peuvent profiter de notre tarif client fondateur de {promoPrice} $ par bouteille de {size}, au lieu du prix régulier de {regular} $.",
      "Lorsque les routes de livraison actuelles auront atteint leur capacité, le tarif régulier s'appliquera aux nouveaux clients.",
    ],
    promotional: "Tarif client fondateur",
    regular: "Prix régulier",
    save: "Vous économisez",
    perBottle: "par bouteille de 18 L",
    rebatePer: "par bouteille",
    terms: [
      "{promoPrice} $ par bouteille de {size}",
      "Prix régulier : {regular} $",
      "Commande minimale de 4 bouteilles",
      "Livraison résidentielle et commerciale",
      "Livraison hebdomadaire, aux deux semaines ou mensuelle",
      "Aucuns frais d'installation",
      "Code promo : FOUNDER",
      "Offert tant qu'il reste des places sur nos routes de livraison à Ottawa",
      "Les clients fondateurs en livraison récurrente conservent le prix de {promoPrice} $ par bouteille de {size} tant que leur service récurrent se poursuit",
    ],
    termsNote: "S'applique uniquement à l'eau embouteillée de 18 L. Les dépôts de bouteilles remboursables, distributeurs, équipements et autres produits ou frais ne sont pas réduits.",
    howToTitle: "Comment obtenir le prix de {promoPrice} $",
    howToBody: "Ajoutez vos bouteilles, puis saisissez FOUNDER dans le champ « Code promo » au moment du paiement et appuyez sur Appliquer.",
    lppTitle: "Programme partenaire local",
    lppLead: "Gratuit pour les entreprises qui planifient un minimum de 10 bouteilles par mois.",
    lppBenefits: [
      "Profil d'entreprise sur CapitalFlow.ca",
      "Promotion en vedette des entreprises participantes",
      "Possibilités d'être présenté dans le contenu et les blogues de Capital Flow",
      "Prix et reconnaissance pour les meilleurs partenaires participants",
    ],
    lppCta: "Joindre le Programme partenaire local",
    faqTitle: "Foire aux questions",
    faqQ: "Combien coûte la livraison d'eau de 18 L de Capital Flow avec le tarif client fondateur?",
    faqA: "Tant qu'il reste des places sur nos routes de livraison actuelles à Ottawa, les nouveaux clients paient {promoPrice} $ par bouteille de {size} au lieu du prix régulier de {regular} $ avec le code FOUNDER. La commande minimale est de 4 bouteilles et il n'y a aucuns frais d'installation. Les dépôts de bouteilles remboursables sont distincts et ne sont pas réduits.",
    order: "Commander de l'eau",
    endedH1: "Le tarif client fondateur est maintenant terminé",
    endedBody: "Nos routes de livraison actuelles à Ottawa sont complètes; le tarif régulier s'applique maintenant aux nouveaux clients : {regular} $ par bouteille de 18 L, minimum de quatre bouteilles, aucuns frais d'installation. Les dépôts de bouteilles remboursables sont distincts.",
    endedTitle: "Livraison d'eau à Ottawa | Capital Flow",
    endedDesc: "Capital Flow livre de l'eau embouteillée de 18 L et 11 L partout à Ottawa. Consultez notre offre actuelle et commandez en ligne.",
    endedCta: "Voir l'offre actuelle de livraison d'eau",
    back: "Retour à Capital Flow",
    ribbon: "Tarif client fondateur : {promoPrice} $ par bouteille de {size} avec le code FOUNDER",
    line: "Tarif client fondateur : {promoPrice} $ par bouteille de {size} (prix régulier {regular} $) avec le code promo FOUNDER — tant qu'il reste des places sur nos routes de livraison à Ottawa.",
  },
};

// Offer JSON-LD (only emitted while the promo is active). No validity end date — open-ended.
export function promoJsonLd(view) {
  const price = view.promoPrice.toFixed(2);
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Founding Customer Pricing — Ottawa 18 L Water Delivery",
    serviceType: "Bottled water delivery",
    url: PROMO.pageUrl,
    provider: { "@id": "https://capitalflow.ca/#organization" },
    areaServed: { "@type": "City", name: "Ottawa", containedInPlace: { "@type": "AdministrativeArea", name: "Ontario" } },
    offers: {
      "@type": "Offer",
      name: `Founding Customer Pricing — $${view.promoPrice} per ${PROMO.size} bottle`,
      price,
      priceCurrency: "CAD",
      availability: "https://schema.org/LimitedAvailability",
      url: PROMO.pageUrl,
      seller: { "@id": "https://capitalflow.ca/#organization" },
      eligibleQuantity: { "@type": "QuantitativeValue", minValue: PROMO.minimumOrder, unitText: "bottles" },
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price,
        priceCurrency: "CAD",
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: `${PROMO.size} bottle` },
      },
      description:
        `$${view.promoPrice} per ${PROMO.size} bottle (regularly $${view.regular}) with promo code ${PROMO.code}. ` +
        `Minimum order of ${PROMO.minimumOrder} bottles. Available while delivery spots remain open on our current Ottawa routes. ` +
        "Refundable bottle deposits are separate and not discounted.",
    },
  };
}

export function promoFaqJsonLd(view) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [{
      "@type": "Question",
      name: PROMO_TEXT.en.faqQ,
      acceptedAnswer: { "@type": "Answer", text: resolvePromoText("en", view.vars).faqA },
    }],
  };
}
